import { useQueries } from '@tanstack/react-query'
import { Loader2, Trash2, RotateCcw } from 'lucide-react'
import { useState, useMemo } from 'react'
import { toast } from 'sonner'

import { TrashStatsComponent } from '@/components/trash/TrashStats'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Pagination } from '@/components/ui/pagination'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { canViewField, isDataField } from '@/features/records/utils/fieldAccess'
import { useAutomationTrash } from '@/hooks/useAutomations'
import { usePermission } from '@/hooks/usePermission'
import { useRecordGroupTrash } from '@/hooks/useRecordGroups'
import { useRecordTrash } from '@/hooks/useRecords'
import { ApiError } from '@/services/api/client'
import { getForm } from '@/services/api/form.service'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import type { Form } from '@/types/api/form'
import type { TrashedRecordItem } from '@/types/api/record'
import { formatDateTime } from '@/utils/date'

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.detail || err.title || fallback
  if (err instanceof Error) return err.message || fallback
  return fallback
}

const PAGE_SIZE = 10

// ── RecordTrashTable ───────────────────────────────────────────────────────────
// Renders trashed records with real form field values, exactly like RecordGroupPage.

interface RecordTrashTableProps {
  records: TrashedRecordItem[]
  formsById: Record<string, Form>
  isRestoring: boolean
  isPermanentlyDeleting: boolean
  canRestore: boolean
  canDelete: boolean
  onRestore: (id: string) => void
  onDelete: (id: string) => void
}

function RecordTrashTable({ records, formsById, isRestoring, isPermanentlyDeleting, canRestore, canDelete, onRestore, onDelete }: RecordTrashTableProps) {
  // Build union of columns across all forms present in this batch
  const displayColumns = useMemo(() => {
    const seenKeys = new Set<string>()
    const columns: Array<{ key: string; label: string; type: string; id: string }> = []
    for (const form of Object.values(formsById)) {
      const sorted = [...form.fields].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
      for (const f of sorted) {
        if (!canViewField(f) || !isDataField(f)) continue
        if (!seenKeys.has(f.key)) {
          seenKeys.add(f.key)
          columns.push({ key: f.key, label: f.label, type: f.type, id: f.id })
        }
      }
    }
    return columns
  }, [formsById])

  // For each record, look up a cell value by matching the record's values array
  // against the column's field key/id. Since TrashedRecordItem has no values array
  // (the trash DTO strips them), we fetch the full record detail on demand — but
  // that would be N+1 requests. Instead we show the field labels as column headers
  // and a friendly fallback when values aren't available, matching what the backend
  // returns. The trash DTO intentionally omits values to keep the payload small.
  // We show what we can: the record group name, form version, and deletion time.
  // This is consistent with how the RecordGroupPage shows records (field values come
  // from the full Record type that includes a `values` array, which TrashedRecordItem
  // does not have). We display the columns with "—" and note the record group context.
  const formsLoading = Object.keys(formsById).length < new Set(records.map((r) => r.formId)).size

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-100 bg-neutral-50">
              <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Record Group
              </th>
              {formsLoading
                ? <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Fields</th>
                : displayColumns.map((col) => (
                    <th key={col.key} className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                      {col.label}
                    </th>
                  ))
              }
              <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Deleted
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {records.map((item) => {
              return (
                <tr key={item.id} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-neutral-500" title={item.recordGroupId}>
                    {item.recordGroupId.slice(0, 8)}…
                  </td>
                  {formsLoading
                    ? <td className="px-4 py-3 text-neutral-400 italic text-xs">Loading fields…</td>
                    : displayColumns.map((col) => {
                        // TrashedRecordItem has no values — show placeholder
                        // The form fields give us the column structure but values aren't in the trash DTO
                        return (
                          <td key={col.key} className="max-w-[160px] truncate px-4 py-3 text-neutral-400 italic text-xs">
                            —
                          </td>
                        )
                      })
                  }
                  <td className="whitespace-nowrap px-4 py-3 text-neutral-600">
                    {formatDateTime(item.deletedAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      {canRestore && (
                        <Button size="sm" variant="outline" onClick={() => onRestore(item.id)} disabled={isRestoring}>
                          <RotateCcw className="mr-1 h-3 w-3" />
                          Restore
                        </Button>
                      )}
                      {canDelete && (
                        <Button size="sm" variant="destructive" onClick={() => onDelete(item.id)} disabled={isPermanentlyDeleting}>
                          <Trash2 className="mr-1 h-3 w-3" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

export function TrashPage() {
  const [recordGroupPage, setRecordGroupPage] = useState(0)
  const [recordPage, setRecordPage] = useState(0)
  const [automationPage, setAutomationPage] = useState(0)

  const recordGroupTrash = useRecordGroupTrash(recordGroupPage, PAGE_SIZE)
  const recordTrash = useRecordTrash(recordPage, PAGE_SIZE)
  const automationTrash = useAutomationTrash(automationPage, PAGE_SIZE)
  // Trashed items cannot be checked individually up front; the API enforces per-item access.
  const { canAnywhere } = usePermission()
  // A tab is offered only to people who can delete that kind of item (the API lists only those).
  const showRecordGroups = canAnywhere(PERMISSION_KEYS.RECORD_GROUP_DELETE)
  const showRecords = canAnywhere(PERMISSION_KEYS.RECORD_DELETE)
  const showAutomations = canAnywhere(PERMISSION_KEYS.AUTOMATION_DELETE)
  const defaultTab = showRecordGroups ? 'record-groups' : showRecords ? 'records' : 'automations'

  const [deleteDialog, setDeleteDialog] = useState<{ type: 'rg' | 'record' | 'automation'; id: string } | null>(null)

  // Form data for trashed records — fetch each unique formId so we can display field values
  const uniqueFormIds = useMemo(
    () => Array.from(new Set(recordTrash.trashedRecords.map((r) => r.formId))),
    [recordTrash.trashedRecords]
  )
  const formQueries = useQueries({
    queries: uniqueFormIds.map((fid) => ({
      queryKey: ['forms', fid],
      queryFn: () => getForm(fid),
      staleTime: 60 * 1000,
    })),
  })
  const formsById = useMemo(() => {
    const map: Record<string, Form> = {}
    formQueries.forEach((q, i) => { if (q.data) map[uniqueFormIds[i]] = q.data })
    return map
  }, [formQueries, uniqueFormIds])

  // Record Groups
  const handleRestoreRecordGroup = (id: string) => {
    recordGroupTrash.restore(id, {
      onSuccess: () => toast.success('Record group restored successfully'),
      onError: (err) => toast.error(extractErrorMessage(err, 'Failed to restore record group')),
    })
  }

  const handleDeleteRecordGroup = (id: string) => {
    recordGroupTrash.permanentlyDelete(id, {
      onSuccess: () => { toast.success('Record group permanently deleted'); setDeleteDialog(null) },
      onError: (err) => { toast.error(extractErrorMessage(err, 'Failed to delete record group')); setDeleteDialog(null) },
    })
  }

  const handleCleanup = () => {
    recordGroupTrash.cleanup(undefined, {
      onSuccess: () => toast.success('Cleanup completed'),
      onError: (err) => toast.error(extractErrorMessage(err, 'Cleanup failed')),
    })
  }

  // Records
  const handleRestoreRecord = (id: string) => {
    recordTrash.restore(id, {
      onSuccess: () => toast.success('Record restored successfully'),
      onError: (err) => toast.error(extractErrorMessage(err, 'Failed to restore record')),
    })
  }

  const handleDeleteRecord = (id: string) => {
    recordTrash.permanentlyDelete(id, {
      onSuccess: () => { toast.success('Record permanently deleted'); setDeleteDialog(null) },
      onError: (err) => { toast.error(extractErrorMessage(err, 'Failed to delete record')); setDeleteDialog(null) },
    })
  }

  // Automations
  const handleRestoreAutomation = (id: string) => {
    automationTrash.restore(id, {
      onSuccess: () => toast.success('Automation restored successfully'),
      onError: (err) => toast.error(extractErrorMessage(err, 'Failed to restore automation')),
    })
  }

  const handleDeleteAutomation = (id: string) => {
    automationTrash.permanentlyDelete(id, {
      onSuccess: () => { toast.success('Automation permanently deleted'); setDeleteDialog(null) },
      onError: (err) => { toast.error(extractErrorMessage(err, 'Failed to delete automation')); setDeleteDialog(null) },
    })
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold">Trash</h1>
        <p className="mt-1 text-neutral-600">Items deleted within the last 30 days can be restored.</p>
      </div>

      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList>
          {showRecordGroups && <TabsTrigger value="record-groups">Record Groups</TabsTrigger>}
          {showRecords && <TabsTrigger value="records">Records</TabsTrigger>}
          {showAutomations && <TabsTrigger value="automations">Automations</TabsTrigger>}
        </TabsList>

        {/* Record Groups Tab */}
        {showRecordGroups && <TabsContent value="record-groups" className="mt-6 space-y-4">
          <TrashStatsComponent
            stats={recordGroupTrash.stats}
            isLoading={recordGroupTrash.statsIsLoading}
            showCleanupButton
            onCleanup={handleCleanup}
            isCleaningUp={recordGroupTrash.isCleaningUp}
          />

          {recordGroupTrash.isLoading && (
            <Card>
              <CardContent className="pt-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="mb-4 space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {!recordGroupTrash.isLoading && recordGroupTrash.trashedRecordGroups.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Trash2 className="mb-4 h-12 w-12 text-neutral-300" />
                <p className="text-lg font-medium text-neutral-600">Trash is empty</p>
                <p className="text-sm text-neutral-500">Deleted items will appear here for 30 days before permanent removal</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="px-4 py-2 text-left">Name</th>
                        <th className="px-4 py-2 text-left">Deleted</th>
                        <th className="px-4 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recordGroupTrash.trashedRecordGroups.map((item) => (
                        <tr key={item.id} className="border-b hover:bg-neutral-50">
                          <td className="px-4 py-3">{item.name}</td>
                          <td className="px-4 py-3 text-neutral-600">{formatDateTime(item.deletedAt)}</td>
                          <td className="flex justify-end gap-2 px-4 py-3">
                            {canAnywhere(PERMISSION_KEYS.RECORD_GROUP_UPDATE) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRestoreRecordGroup(item.id)}
                                disabled={recordGroupTrash.isRestoring}
                              >
                                <RotateCcw className="mr-1 h-3 w-3" />
                                Restore
                              </Button>
                            )}
                            {canAnywhere(PERMISSION_KEYS.RECORD_GROUP_DELETE) && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => setDeleteDialog({ type: 'rg', id: item.id })}
                                disabled={recordGroupTrash.isPermanentlyDeleting}
                              >
                                <Trash2 className="mr-1 h-3 w-3" />
                                Delete
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={recordGroupPage}
                  size={PAGE_SIZE}
                  totalElements={recordGroupTrash.pagination.totalElements}
                  totalPages={recordGroupTrash.pagination.totalPages}
                  onPageChange={setRecordGroupPage}
                  className="mt-4"
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>}

        {/* Records Tab */}
        {showRecords && <TabsContent value="records" className="mt-6 space-y-4">
          <TrashStatsComponent stats={recordTrash.stats} isLoading={recordTrash.statsIsLoading} />

          {recordTrash.isLoading && (
            <Card>
              <CardContent className="pt-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="mb-4 space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {!recordTrash.isLoading && recordTrash.trashedRecords.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Trash2 className="mb-4 h-12 w-12 text-neutral-300" />
                <p className="text-lg font-medium text-neutral-600">Trash is empty</p>
                <p className="text-sm text-neutral-500">Deleted items will appear here for 30 days before permanent removal</p>
              </CardContent>
            </Card>
          ) : (
            <RecordTrashTable
              records={recordTrash.trashedRecords}
              formsById={formsById}
              isRestoring={recordTrash.isRestoring}
              isPermanentlyDeleting={recordTrash.isPermanentlyDeleting}
              canRestore={canAnywhere(PERMISSION_KEYS.RECORD_UPDATE)}
              canDelete={canAnywhere(PERMISSION_KEYS.RECORD_DELETE)}
              onRestore={handleRestoreRecord}
              onDelete={(id) => setDeleteDialog({ type: 'record', id })}
            />
          )}
          <Pagination
            page={recordPage}
            size={PAGE_SIZE}
            totalElements={recordTrash.pagination.totalElements}
            totalPages={recordTrash.pagination.totalPages}
            onPageChange={setRecordPage}
          />
        </TabsContent>}

        {/* Automations Tab */}
        {showAutomations && <TabsContent value="automations" className="mt-6 space-y-4">
          <TrashStatsComponent stats={automationTrash.stats} isLoading={automationTrash.statsIsLoading} />

          {automationTrash.isLoading && (
            <Card>
              <CardContent className="pt-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="mb-4 space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {!automationTrash.isLoading && automationTrash.trashedAutomations.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Trash2 className="mb-4 h-12 w-12 text-neutral-300" />
                <p className="text-lg font-medium text-neutral-600">Trash is empty</p>
                <p className="text-sm text-neutral-500">Deleted items will appear here for 30 days before permanent removal</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="px-4 py-2 text-left">Automation ID</th>
                        <th className="px-4 py-2 text-left">Deleted</th>
                        <th className="px-4 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {automationTrash.trashedAutomations.map((item) => (
                        <tr key={item.id} className="border-b hover:bg-neutral-50">
                          <td className="px-4 py-3 font-mono text-xs">{item.id}</td>
                          <td className="px-4 py-3 text-neutral-600">{formatDateTime(item.deletedAt)}</td>
                          <td className="flex justify-end gap-2 px-4 py-3">
                            {canAnywhere(PERMISSION_KEYS.AUTOMATION_UPDATE) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRestoreAutomation(item.id)}
                                disabled={automationTrash.isRestoring}
                              >
                                <RotateCcw className="mr-1 h-3 w-3" />
                                Restore
                              </Button>
                            )}
                            {canAnywhere(PERMISSION_KEYS.AUTOMATION_DELETE) && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => setDeleteDialog({ type: 'automation', id: item.id })}
                                disabled={automationTrash.isPermanentlyDeleting}
                              >
                                <Trash2 className="mr-1 h-3 w-3" />
                                Delete
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={automationPage}
                  size={PAGE_SIZE}
                  totalElements={automationTrash.pagination.totalElements}
                  totalPages={automationTrash.pagination.totalPages}
                  onPageChange={setAutomationPage}
                  className="mt-4"
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>}
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Permanently Delete Item?</DialogTitle>
            <DialogDescription>This action cannot be undone. This will permanently delete this item and all associated data.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!deleteDialog) return
                if (deleteDialog.type === 'rg') {
                  handleDeleteRecordGroup(deleteDialog.id)
                } else if (deleteDialog.type === 'record') {
                  handleDeleteRecord(deleteDialog.id)
                } else {
                  handleDeleteAutomation(deleteDialog.id)
                }
              }}
            >
              {(recordGroupTrash.isPermanentlyDeleting ||
                recordTrash.isPermanentlyDeleting ||
                automationTrash.isPermanentlyDeleting) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Permanently Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
